  "use client"

import { useState } from "react"
import { FaTrash } from 'react-icons/fa';
import styles from "./css/allergies.module.css"
import { updateConsultation } from "../../../../api/clinic/consultationApi.js";

export default function Allergies({ 
  consultationId, 
  patientData = {}, 
  initialData,
  onDataChange,
  ensureConsultationExists,
  onSave 
}) {
  const [allergiesOther, setAllergiesOther] = useState(
    initialData?.allergiesOther || [{ description: "", type: "" }]
  )
  const [allergiesSingleDrug, setAllergiesSingleDrug] = useState(
    initialData?.allergiesSingleDrug || [{ drug: "" }]
  )
  const [allergiesTherapeutic, setAllergiesTherapeutic] = useState(
    initialData?.allergiesTherapeutic || [{ drugGroup: "" }]
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [saveSuccess, setSaveSuccess] = useState(false)
  
  const notifyChange = () => {
    if (onDataChange) onDataChange()
  }

  const addAllergiesOther = () => {
    setAllergiesOther([...allergiesOther, { description: "", type: "" }])
    notifyChange()
  }

  const removeAllergiesOther = (index) => {
    setAllergiesOther(allergiesOther.filter((_, i) => i !== index))
    notifyChange()
  }

  const addAllergiesSingleDrug = () => {
    setAllergiesSingleDrug([...allergiesSingleDrug, { drug: "" }])
    notifyChange()
  }

  const removeAllergiesSingleDrug = (index) => {
    setAllergiesSingleDrug(allergiesSingleDrug.filter((_, i) => i !== index))
    notifyChange()
  }

  const addAllergiesTherapeutic = () => {
    setAllergiesTherapeutic([...allergiesTherapeutic, { drugGroup: "" }])
    notifyChange()
  }

  const removeAllergiesTherapeutic = (index) => {
    setAllergiesTherapeutic(allergiesTherapeutic.filter((_, i) => i !== index))
    notifyChange()
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError("")
      setSaveSuccess(false)

      // Ensure consultation exists (creates if needed)
      const activeConsultationId = await ensureConsultationExists()

      // Filter out empty entries and prepare data as arrays
      const validOther = allergiesOther.filter(a => a.description.trim() || a.type.trim())
      const validSingleDrug = allergiesSingleDrug.filter(a => a.drug.trim())
      const validTherapeutic = allergiesTherapeutic.filter(a => a.drugGroup.trim())

      const payload = {
        allergydescription: validOther.map(a => a.description),
        allergytype: validOther.map(a => a.type),
        allergymedication: validSingleDrug.map(a => a.drug),
        allergytherapeuticclass: validTherapeutic.map(a => a.drugGroup),
      }

      await updateConsultation(activeConsultationId, payload)

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      
      if (onSave) {
        onSave({ allergiesOther, allergiesSingleDrug, allergiesTherapeutic })
      }
    } catch (e) {
      setSaveError(e.message || "Failed to save data")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.sectionsContainer}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Allergies Other</h3>
            <button className={styles.addButton} onClick={addAllergiesOther}>
              +
            </button>
          </div>

          <div className={styles.tableHeader}>
            <span className={styles.headerCell}>Allergy Description</span>
            <span className={styles.headerCell}>Allergy Type</span>
          </div>

          {allergiesOther.map((allergy, index) => (
            <div key={index} className={styles.tableRow}>
              <input
                type="text"
                className={styles.input}
                value={allergy.description}
                onChange={(e) => {
                  const newAllergies = [...allergiesOther]
                  newAllergies[index].description = e.target.value
                  setAllergiesOther(newAllergies)
                  notifyChange()
                }}
              />
              <input
                type="text"
                className={styles.input}
                value={allergy.type}
                onChange={(e) => {
                  const newAllergies = [...allergiesOther]
                  newAllergies[index].type = e.target.value
                  setAllergiesOther(newAllergies)
                  notifyChange()
                }}
              />
              <button className={styles.deleteButton} onClick={() => removeAllergiesOther(index)}>
                <FaTrash color="red" />
              </button>
            </div>
          ))}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Allergies Single Drug</h3>
            <button className={styles.addButton} onClick={addAllergiesSingleDrug}>
              +
            </button>
          </div>

          <div className={styles.tableHeader}>
            <span className={styles.headerCell}>Drug</span>
          </div>

          {allergiesSingleDrug.map((allergy, index) => (
            <div key={index} className={styles.tableRow}>
              <input
                type="text"
                className={styles.input}
                value={allergy.drug}
                onChange={(e) => {
                  const newAllergies = [...allergiesSingleDrug]
                  newAllergies[index].drug = e.target.value
                  setAllergiesSingleDrug(newAllergies)
                  notifyChange()
                }}
              />
              <button className={styles.deleteButton} onClick={() => removeAllergiesSingleDrug(index)}>
                <FaTrash color="red" />
              </button>
            </div>
          ))}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Allergies Therapeutic Class</h3>
            <button className={styles.addButton} onClick={addAllergiesTherapeutic}>
              +
            </button>
          </div>

          <div className={styles.tableHeader}>
            <span className={styles.headerCell}>Drug Group</span>
          </div>

          {allergiesTherapeutic.map((allergy, index) => (
            <div key={index} className={styles.tableRow}>
              <input
                type="text"
                className={styles.input}
                value={allergy.drugGroup}
                onChange={(e) => {
                  const newAllergies = [...allergiesTherapeutic]
                  newAllergies[index].drugGroup = e.target.value
                  setAllergiesTherapeutic(newAllergies)
                  notifyChange()
                }}
              />
              <button className={styles.deleteButton} onClick={() => removeAllergiesTherapeutic(index)}>
                <FaTrash color="red" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {saveError && (
        <div className={styles.inlineError}>
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className={styles.successMessage}>
          Data saved successfully!
        </div>
      )}

      <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  )
}